import { useCallback, useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import {
  createSubscriptionFormSchema,
  SubscriptionFormDefaultValues,
  SubscriptionFormKeyEnum,
} from "./util/subscriptionForm";
import { yupResolver } from "@hookform/resolvers/yup";
import { styled } from "@mui/material";
import { GoogleReCaptcha } from "react-google-recaptcha-v3";
import { useTranslations } from "next-intl";
import { useRouter } from "next/router";
import { useSubscriptionForm } from "./hooks/useSubscription";
import { ModalMessage } from "@components/message-modal";
import useDataLayerEvent from "custom-hooks/data-layer-event.hook";

// Añadimos intervalo de refresco automático del captcha (tokens ~2min)
const CAPTCHA_REFRESH_INTERVAL_MS = 110000; // 1m50s

const SubscriptionForm = () => {
  const { locale } = useRouter();
  const t = useTranslations("SubscriptionForm");
  const schema = createSubscriptionFormSchema(locale as any);
  const [refreshCaptcha, setRefreshCaptcha] = useState<boolean>(false);
  const { event } = useDataLayerEvent();

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<any>({
    defaultValues: SubscriptionFormDefaultValues,
    resolver: yupResolver(schema) as any,
  });

  const { mutateAsync, isLoading, message, isOpen, setIsOpen } =
    useSubscriptionForm();

  useEffect(() => {
    const interval = setInterval(() => {
      setRefreshCaptcha(prev => !prev);
    }, CAPTCHA_REFRESH_INTERVAL_MS);

    return () => clearInterval(interval);
  }, []);

  const onSubmit: SubmitHandler<any> = async (data, e) => {
    setRefreshCaptcha(prev => !prev);
    await mutateAsync(data).then((res) => {
      event({
        event_name: "newsletter_subscription",
        body: {
          email: data.email,
          name: data.name,
          interests: data.interests,
        },
      });
    })
  };

  const optionsCheck = [
    {
      label: t("form.options.marketing"),
      value: "1",
    },
    {
      label: t("form.options.sector"),
      value: "2",
    },
    {
      label: t("form.options.finance"),
      value: "3",
    },
    {
      label: t("form.options.news"),
      value: "4",
    },
    // {
    //   label: t("form.options.others"),
    //   value: "5",
    // },
  ];

  const handleVerify = useCallback(
    (token: string) => {
      setValue(SubscriptionFormKeyEnum.captcha as any, token, {
        shouldValidate: true,
      });
    },
    [setValue]
  );

  return (
    <CustomForm onSubmit={handleSubmit(onSubmit)}>
      <ModalMessage
        {...message}
        variant={message.isError ? "error" : "success"}
        open={isOpen}
        buttonText="OK"
        handleClose={() => setIsOpen(false)}
      />
      <div className="input-form-container">
        <label
          className="input-form-label"
          htmlFor={SubscriptionFormKeyEnum.email}
        >
          {t("form.field.email")}
        </label>
        <input
          {...register(SubscriptionFormKeyEnum.email)}
          type="email"
          placeholder={t("form.field.email")}
          className="input-form"
        />
        {errors.email && (
          <p className="input-form-error">{errors.email.message as string}</p>
        )}
      </div>
      <div className="input-form-container">
        <label
          className="input-form-label"
          htmlFor={SubscriptionFormKeyEnum.name}
        >
          {t("form.field.name")}
        </label>

        <input
          {...register(SubscriptionFormKeyEnum.name)}
          type="text"
          placeholder={t("form.field.name")}
          className="input-form"
        />
        {errors.name && (
          <p className="input-form-error">{errors.name.message as string}</p>
        )}
      </div>
      <div className="input-form-container">
        <label
          className="input-form-label"
          htmlFor={SubscriptionFormKeyEnum.interests}
        >
          {t("form.field.interests")}
        </label>
        {optionsCheck.map((option, index) => {
          return (
            <div key={index} className="input-form-checkbox">
              <input
                {...register(SubscriptionFormKeyEnum.interests)}
                type="checkbox"
                value={option.value}
              />
              <label htmlFor={option.value}>{option.label}</label>
            </div>
          );
        })}
        {errors.interests && (
          <p className="input-form-error">
            {errors.interests?.message as string}
          </p>
        )}
      </div>

      <input type="hidden" {...register(SubscriptionFormKeyEnum.captcha)} />

      <GoogleReCaptcha onVerify={handleVerify} refreshReCaptcha={refreshCaptcha} />
      <button type="submit" className="input-form-submit" disabled={isLoading}>
        {isLoading ? t("form.field.submit_loading") : t("form.field.submit")}
      </button>
    </CustomForm>
  );
};

export default SubscriptionForm;

const CustomForm = styled("form")`
  margin-top: 1rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  .input-form-container {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    .input-form {
      width: 100%;
      padding: 0.7rem;
      border: 1px solid #ccc;
      border-radius: 0.5rem;
    }
    .input-form-label {
      font-size: 0.8rem;
      font-weight: 500;
      color: white;

      &:after {
        content: " *";
        color: var(--color-brand-red-main);
        margin-right: 0.1rem;
      }
    }
    .input-form-error {
      color: var(--color-brand-red-main);
      font-size: 0.8rem;
      font-weight: 400;
    }
  }

  .input-form-checkbox {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    cursor: pointer;
    label {
      font-size: 0.8rem;
      font-weight: 400;
      color: white;
    }
  }
  .input-form-submit {
    background-color: var(--color-brand-red-main);
    color: white;
    border: none;
    padding: 0.7rem;
    border-radius: 0.5rem;
    cursor: pointer;
    font-size: 0.8rem;
    font-weight: 600;
    max-width: 100px;
  }
`;
